import apiClient from '@/lib/api';

// Admin service for the Changelog section.
//
// Entries are hardcoded server-side in back-end-next/src/lib/changelog/entries.ts.
// Metric maths lives in PostgreSQL functions (get_changelog_*), per METRICS.md:
// analytics are never calculated in app code.
//
// There is no range control. Every chart spans one month before the change
// through to today. The month before is what makes the change marker mean
// anything: without it the change sits on the first point with nothing to
// compare against.
//
// Design: docs/specs/2026-08-21-changelog-impact-design.md

export type ChangelogArea =
  | 'signup'
  | 'pricing'
  | 'onboarding'
  | 'forms'
  | 'billing'
  | 'xray';

export interface ChangelogListItem {
  slug: string;
  date: string;
  area: ChangelogArea;
  title: string;
  /** The team's notes on the entry, shown as a snippet under the title. */
  notes: string | null;
}

export interface MetricPoint {
  date: string;
  /** Null when the bucket had no signups. Never 0: an empty week is not a collapse. */
  value: number | null;
  numerator: number;
  denominator: number;
  isComplete: boolean;
  /**
   * Count per signup method (email / google / slack / other). Only the
   * `signups` count metric sends it; rate metrics leave it out.
   */
  breakdown?: Record<string, number>;
}

/**
 * One cell of the before/since cohort table: the accounts that converted
 * between `fromDay` and `toDay` days after signing up. `eligible` is the
 * subset of `signups` old enough to have reached `toDay`; `value` is over
 * eligible, never over signups, so a young cohort reads "31% of 29" rather
 * than a collapsing rate. The bucket starting at 0 and ending on the largest
 * day is the cumulative total.
 */
export interface CohortRow {
  cohort: 'before' | 'since';
  signups: number;
  fromDay: number;
  /** Null is the uncapped "so far" bucket: everyone eligible, still growing. */
  toDay: number | null;
  eligible: number;
  converted: number;
  value: number | null;
  /** Total MRR behind the cell. Money cohorts only; absent on rate cohorts. */
  amount?: number;
}

/**
 * One account behind a cohort bar: a member of that cell's numerator, someone
 * who signed up in the cohort and started a paid plan inside the bucket.
 */
export interface CohortMember {
  /** Public id, so the row links straight through to /agencies/:id. */
  agencyId: string;
  name: string;
  signedUpAt: string;
  convertedAt: string;
  daysToConvert: number;
  /** Null on an account whose plan_started event predates the metadata. */
  planName: string | null;
  /** 'trialing' or 'active' as Stripe reported it at the time. */
  planStatus: string | null;
  /** Monthly value of the price this account is actually billed on. */
  mrr: number;
  /** False for a trial that has not been charged yet. */
  isPaying: boolean;
}

export interface CohortMembers {
  metric: string;
  cohort: 'before' | 'since';
  fromDay: number;
  toDay: number | null;
  spanDays: number;
  agencies: CohortMember[];
}

export interface CohortTable {
  /** Days of signups before the change that form the "before" cohort. */
  spanDays: number;
  rows: CohortRow[];
  /** Whether a bar opens the accounts behind it. Absent on older payloads: yes. */
  drillDown?: boolean;
  /** Heading over the bars, when the default for the unit doesn't fit. */
  caption?: string | null;
}

/** What the counts behind a point or a bar are ("6 of 8 trials"). */
export interface MetricPopulation {
  one: string;
  many: string;
  /** Opens the note under the chart: "<newest> after that have not had their N days yet." */
  newest: string;
}

export interface ImpactMetric {
  key: string;
  label: string;
  description: string;
  unit: 'percent' | 'count' | 'currency';
  /** Lookforward for cohort metrics. Null when the metric has none. */
  windowDays: number | null;
  /** Each point aggregates this many trailing days, ending on the point's date. */
  trailingDays: number;
  series: MetricPoint[];
  /**
   * Last date the series can speak to. Incomplete buckets are dropped by the
   * API, so a chart ending well before today is correct rather than missing
   * data: a cohort measured over N days cannot be read until N days pass.
   */
  completeThrough: string | null;
  /** Before/since cohort table. Null for metrics that do not declare one. */
  cohorts: CohortTable | null;
  /** Absent on older payloads, which always counted accounts. */
  population?: MetricPopulation;
}

export interface ChangelogNotes {
  body: string;
  updatedAtUtc: string;
  /** Full name of whoever saved last. Null once that user has been deleted. */
  updatedBy: string | null;
}

export interface Confounder {
  slug: string;
  date: string;
  title: string;
  area: ChangelogArea;
}

export interface ChangelogDetail {
  slug: string;
  date: string;
  area: ChangelogArea;
  title: string;
  description: string;
  metrics: ImpactMetric[];
  confounders: Confounder[];
  /** The team's free-text notes on this entry. Null until someone writes some. */
  notes: ChangelogNotes | null;
}

export const changelogService = {
  async list(): Promise<ChangelogListItem[]> {
    const res = await apiClient.get('/admin/changelog');
    return res.data.data.entries;
  },

  async detail(slug: string): Promise<ChangelogDetail | null> {
    try {
      const res = await apiClient.get(`/admin/changelog/${slug}`);
      return res.data.data;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },

  /**
   * The accounts behind one bar of the cohort chart. Fetched on click rather
   * than shipped with the detail payload: most visits never open the panel.
   */
  async cohortMembers(
    slug: string,
    metric: string,
    cohort: 'before' | 'since',
    fromDay: number,
    toDay: number | null,
  ): Promise<CohortMembers> {
    const res = await apiClient.get(`/admin/changelog/${slug}/cohort-members`, {
      // `metric` decides which population the list covers, so it is not
      // optional: the money bars count payers, the rate bars plan starts.
      params: { metric, cohort, fromDay, toDay: toDay === null ? '' : toDay },
    });
    return res.data.data;
  },

  /** Replaces the notes on an entry. An empty body clears them (returns null). */
  async saveNotes(slug: string, body: string): Promise<ChangelogNotes | null> {
    const res = await apiClient.patch(`/admin/changelog/${slug}/notes`, { body });
    return res.data.data.notes;
  },
};
